import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, isRef as _isRef, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "util--hidden" }
const _hoisted_2 = ["src"]

import {computed, ref} from "vue";
import Card from "@/components/layout/Card.vue";
import {useI18n} from "vue3-i18n";
import {useMemberStore} from "@/stores/memberStore";

// jsPDF auto-table documentation:
// https://simonbengtsson.github.io/jsPDF-AutoTable/
// https://github.com/simonbengtsson/jsPDF-AutoTable/blob/master/examples/examples.js
import {jsPDF} from "jspdf";
import autoTable from "jspdf-autotable";
import {Role} from "@/types";

const pageWidth = 279; //mm -> value from their autocrat template.
const pageHeight = 216; //mm


export default /*#__PURE__*/_defineComponent({
  __name: 'MembershipCertificate',
  setup(__props) {

const {t} = useI18n();

const memberStore = useMemberStore();
const member = computed(() => memberStore.getMember);

//PDF of member doc ==========================================================
function downloadMembershipCertificate() {
  membershipCertificate.value.save(`${t("member.certificate.mine")}_${member.value.memberNumber}_${member.value.organizationName}`.replaceAll(" ", "-") + '.pdf');
}

//Helpers ---------------------------------
const pxToMm = (val: number) => val * 0.2645833333;
const pxToPt = (val: number) => val * (72 / 96);
const getFinalYautoTable = (doc: jsPDF) => (doc as any).lastAutoTable.finalY || 0;

//Properties -----------------
let headerImg = ref();
let logo = ref();
let signaturePresident = ref();
let signatureDirector = ref();

let areImagesLoaded = ref(false);

const colors = {
  black: "#000000",
  blue: "#00ADEE",
  white: "#FFFFFF",
};

const pageXPadding = pxToMm(120);
const pageYPadding = pxToMm(120);

const largeYPadding = pxToMm(40);
const mediumYPadding = pxToMm(24);
const smallYPadding = pxToMm(12);

let yPosition = 0;

const titleFontSize = pxToPt(48);
const subtitleFontSize = pxToPt(22);
const dynamicValueFontSize = pxToPt(32);
const signatureLabelFontSize = pxToPt(14);

const basicTableProperties: any = {
  theme: "plain",
  margin: {
    top: pageYPadding,
    right: pageXPadding,
    left: pageXPadding,
  },
};

const basicTextStylesProperties: any = {
  font: "helvetica",
  textColor: colors.black,
  cellPadding: 0,
  halign: "center",
};

//Setting up --------------------------------------------
let doc = new jsPDF();

let membershipCertificate = computed(() => {
  //reset
  yPosition = 0;
  doc = new jsPDF({
    orientation: "landscape",
    format: [pageWidth, pageHeight],
  });

  if (member.value && areImagesLoaded.value) {
    let memberName = memberStore.hasRole(Role.RegularIndividual)
      ? member.value.firstName + " " + member.value.lastName
      : member.value.organizationName;

    //Basic properties
    doc.setProperties({
      title: `${t("member.certificate.mine")} - ${member.value.memberNumber} - ${memberName}`,
      author: "RGCQ",
      creator: "RGCQ",
    });

    //populate with content
    addPdfHeader();

    //Add body
    addTextToPdfBody(t("member.certificate.membershipCertificate"), true, false, "lg");
    addTextToPdfBody(t("member.certificate.toRGCQ"), false, true);
    addTextToPdfBody(t("member.certificate.for"), false, true, "sm");
    addTextToPdfBody(`${member.value.memberNumber} - ${memberName}`, false, false);
    addTextToPdfBody(t("member.certificate.until"), false, true);
    addTextToPdfBody(member.value.subscription?.endAt ?? "", false, false);

    //Add footer
    addPdfFooter();
  }

  return doc;
});

const previewSrc = computed(() => membershipCertificate.value?.output("datauristring"));

//Creating the layout -----------------------------------------------
const addPdfHeader = () => {
  autoTable(doc, {
    margin: {
      top: 0,
      right: 0,
      left: 0,
    },
    body: [[{content: ""}]],
    didDrawCell: (data: any) => {
      if (headerImg.value != null) {
        //calculate so img is fullscreen.
        let ratio = headerImg.value.naturalHeight / headerImg.value.naturalWidth;

        if (!isNaN(pageWidth) && !isNaN(pageWidth * ratio)) {
          doc.addImage(
            headerImg.value,
            "JPEG",
            data.cell.x,
            data.cell.y,
            pageWidth,
            pageWidth * ratio
          );

          yPosition = pageWidth * ratio; //y is now height of img, to place the text under it.
        }
      }
    },
  });
};

const addTextToPdfBody = (text: string, isTitle = false, isSubtitle = false, spacing = "md",) => {
  const startY = yPosition + (spacing == "lg"
    ? largeYPadding
    : spacing == "md"
      ? mediumYPadding
      : smallYPadding);

  autoTable(doc, {
    ...basicTableProperties,
    startY: startY,
    styles: {
      ...basicTextStylesProperties,
      fontStyle: isSubtitle ? "normal" : "bold",
      fontSize: isTitle ? titleFontSize : isSubtitle ? subtitleFontSize : dynamicValueFontSize,
    },
    body: [[{content: text.toUpperCase()}]]
  });

  yPosition = getFinalYautoTable(doc);
};

const addPdfFooter = () => {
  const cellStyles = {
    cellWidth: (pageWidth - (pageXPadding * 2)) / 3,
    halign: "center"
  }

  autoTable(doc, {
    ...basicTableProperties,
    startY: yPosition + mediumYPadding,
    body: [
      [
        {
          content: "",
          title: "signature-president",
          styles: {...cellStyles}
        },
        {
          content: "",
          title: "logo",
          styles: {...cellStyles}
        },
        {
          content: "",
          title: "signature-director",
          styles: {...cellStyles}
        }
      ]
    ],
    didDrawCell: (data: any) => {
      const isLogo = data.cell.raw.title == "logo";
      const isSignatureDirector = data.cell.raw.title == "signature-director";

      const image = isLogo
        ? logo.value
        : isSignatureDirector
          ? signatureDirector.value
          : signaturePresident.value;

      if (image != null && data.cell != null) {
        //add image
        const ratio = image.naturalHeight / image.naturalWidth;
        const signatureCellWidth = cellStyles.cellWidth - pxToMm(72);

        let imageWidth = isLogo
          ? cellStyles.cellWidth
          : signatureCellWidth;
        let imageHeight = imageWidth * ratio;


        if (imageWidth < imageHeight && !isLogo) {
          imageHeight = signatureCellWidth;
          imageWidth = imageHeight * ratio;
        }

        const lineYPosition = yPosition + pxToMm(72);
        const imageYPosition = isLogo
          ? data.cell.y
          : lineYPosition - imageHeight;
        const imageXPosition = isSignatureDirector
          ? data.cell.x + pxToMm(72)
          : data.cell.x;


        if (!isNaN(imageXPosition) && !isNaN(imageYPosition) && !isNaN(imageWidth) && !isNaN(imageHeight)) {
          doc.addImage(
            image,
            "JPEG",
            imageXPosition,
            imageYPosition,
            imageWidth,
            imageHeight
          );

          if (!isLogo) {
            //add line under signature
            doc.setLineWidth(pxToPt(0.5));
            doc.setDrawColor(colors.black);
            doc.line(
              imageXPosition,
              lineYPosition,
              imageXPosition + signatureCellWidth,
              lineYPosition);

            //then add text under it.
            const textYPosition = lineYPosition + doc.getLineWidth() + pxToMm(24);

            doc.setFontSize(signatureLabelFontSize);
            doc.text(
              isSignatureDirector
                ? ["M. Yves Nadon", t("member.certificate.director")]
                : ["Me Yves Joli-Coeur", t("member.certificate.president")],
              imageXPosition,
              textYPosition,
              {
                maxWidth: signatureCellWidth
              });
          }
        }
      }
    },
  });
};

return (_ctx: any,_cache: any) => {
  return (member.value)
    ? (_openBlock(), _createBlock(Card, {
        key: 0,
        title: _unref(t)('member.certificate.mine'),
        "download-label": _unref(t)('member.certificate.download'),
        onDownload: _cache[1] || (_cache[1] = ($event: any) => (downloadMembershipCertificate()))
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("img", {
              ref_key: "headerImg",
              ref: headerImg,
              src: "/images/membership-certificate/header-img.jpg",
              alt: ""
            }, null, 512),
            _createElementVNode("img", {
              ref_key: "logo",
              ref: logo,
              src: "/images/membership-certificate/logo.jpg",
              alt: ""
            }, null, 512),
            _createElementVNode("img", {
              ref_key: "signatureDirector",
              ref: signatureDirector,
              src: "/images/membership-certificate/signature--director.jpg",
              alt: ""
            }, null, 512),
            _createElementVNode("img", {
              ref_key: "signaturePresident",
              ref: signaturePresident,
              src: "/images/membership-certificate/signature--president.jpg",
              alt: "",
              onLoad: _cache[0] || (_cache[0] = () => {_isRef(areImagesLoaded) //@ts-ignore
 ? areImagesLoaded.value = true : areImagesLoaded = true})
            }, null, 544)
          ]),
          _createElementVNode("iframe", { src: previewSrc.value }, null, 8, _hoisted_2)
        ]),
        _: 1
      }, 8, ["title", "download-label"]))
    : _createCommentVNode("", true)
}
}

})